/**
 * Small pixel painter over a 2D canvas. Every call works in whole texture
 * pixels so sprites stay crisp when the scene scales them by an integer.
 */
export type Sprite = Readonly<{
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /** Pixel inside the sprite that sits on the entity's world position. */
  anchorX: number;
  anchorY: number;
}>;

export class Painter {
  readonly #context: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;

  constructor(context: CanvasRenderingContext2D, width: number, height: number) {
    this.#context = context;
    this.width = width;
    this.height = height;
  }

  rect(x: number, y: number, width: number, height: number, color: string): void {
    if (width <= 0 || height <= 0) return;
    this.#context.fillStyle = color;
    this.#context.fillRect(Math.round(x), Math.round(y), Math.round(width), Math.round(height));
  }

  px(x: number, y: number, color: string): void {
    this.rect(x, y, 1, 1, color);
  }

  hline(x: number, y: number, width: number, color: string): void {
    this.rect(x, y, width, 1, color);
  }

  vline(x: number, y: number, height: number, color: string): void {
    this.rect(x, y, 1, height, color);
  }

  outline(x: number, y: number, width: number, height: number, color: string): void {
    this.hline(x, y, width, color);
    this.hline(x, y + height - 1, width, color);
    this.vline(x, y, height, color);
    this.vline(x + width - 1, y, height, color);
  }

  /** A filled box with a one pixel outline and a lighter top edge. */
  panel(
    x: number,
    y: number,
    width: number,
    height: number,
    fill: string,
    line: string,
    highlight?: string,
  ): void {
    this.rect(x, y, width, height, fill);
    this.outline(x, y, width, height, line);
    if (highlight) this.hline(x + 1, y + 1, width - 2, highlight);
  }

  /** Alternating pixels, used for fabric and carpet texture. */
  dither(x: number, y: number, width: number, height: number, color: string, step = 2): void {
    for (let row = 0; row < height; row += 1) {
      for (let column = (row % step); column < width; column += step) {
        this.px(x + column, y + row, color);
      }
    }
  }

  /** Copies another sprite by its top-left corner. */
  blit(sprite: Sprite, x: number, y: number): void {
    this.#context.drawImage(sprite.canvas, Math.round(x), Math.round(y));
  }

  /** Copies another sprite mirrored horizontally. */
  blitMirrored(sprite: Sprite, x: number, y: number): void {
    const context = this.#context;
    context.save();
    context.translate(Math.round(x) + sprite.width, Math.round(y));
    context.scale(-1, 1);
    context.drawImage(sprite.canvas, 0, 0);
    context.restore();
  }
}

export function createPixelCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

export function paintSprite(
  width: number,
  height: number,
  draw: (painter: Painter) => void,
  anchor: Readonly<{ x: number; y: number }> = { x: Math.floor(width / 2), y: height },
): Sprite {
  const canvas = createPixelCanvas(width, height);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("2D canvas is unavailable");
  context.imageSmoothingEnabled = false;
  draw(new Painter(context, canvas.width, canvas.height));
  return {
    canvas,
    width: canvas.width,
    height: canvas.height,
    anchorX: anchor.x,
    anchorY: anchor.y,
  };
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

/** Lightens (positive) or darkens (negative) a hex color by a 0..1 amount. */
export function shade(hex: string, amount: number): string {
  const value = hex.replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const number = Number.parseInt(full, 16);
  const r = (number >> 16) & 255;
  const g = (number >> 8) & 255;
  const b = number & 255;
  const mix = (channel: number) =>
    amount >= 0
      ? clampChannel(channel + (255 - channel) * amount)
      : clampChannel(channel * (1 + amount));
  return `#${[mix(r), mix(g), mix(b)]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}
